export type ApiErrorDetails = unknown;

export class StaffApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: ApiErrorDetails;
  readonly retryAfter?: string;

  constructor(input: { status: number; code: string; message: string; details?: ApiErrorDetails; retryAfter?: string }) {
    super(input.message);
    this.name = "StaffApiError";
    this.status = input.status;
    this.code = input.code;
    this.details = input.details;
    this.retryAfter = input.retryAfter;
  }
}

export async function requestJson<T>(path: string, init: RequestInit = {}, csrfToken = ""): Promise<T> {
  const response = await request(path, init, csrfToken);
  const data = await response.json().catch((): unknown => ({}));
  if (!response.ok) throw toStaffApiError(response, data);
  return data as T;
}

export async function requestText<T>(path: string, init: RequestInit = {}, csrfToken = ""): Promise<T> {
  const response = await request(path, init, csrfToken);
  const text = await response.text();
  if (!response.ok) {
    throw new StaffApiError({
      status: response.status,
      code: response.status === 401 ? "unauthorized" : "request_failed",
      message: text || "Request failed",
      retryAfter: response.headers.get("retry-after") ?? undefined,
    });
  }
  return text as T;
}

export function staffErrorMessage(error: unknown): string {
  if (!(error instanceof StaffApiError)) return "Something went wrong. Keep the current work in place and try again after checking the connection.";
  switch (error.code) {
    case "unauthorized":
      return "Your session has expired. Sign in again before continuing.";
    case "forbidden":
    case "store_credit_reversal_forbidden":
    case "pos_store_credit_reversal_forbidden":
      return "This action requires a manager or administrator.";
    case "csrf_failed":
      return "The session security check failed. Refresh the page, then retry deliberately.";
    case "rate_limited":
      return error.retryAfter ? `Too many attempts. Try again after ${error.retryAfter} seconds.` : "Too many attempts. Wait before trying again.";
    case "phase9_disabled":
      return "This Phase 9 workflow is not enabled for this environment.";
    case "validation_failed":
    case "invalid_json":
      return "Check the highlighted fields and try again.";
    case "not_found":
    case "sale_not_found":
    case "item_not_found":
      return "That record was not found. Keep the search terms and try another lookup.";
    case "insufficient_stock":
    case "reservation_stale":
    case "sale_inventory_blocked":
    case "copy_sale_blocked":
      return "Inventory changed before the sale completed. Refresh the cart before continuing.";
    case "insufficient_store_credit":
      return "Available store credit is insufficient for a store-credit-only sale.";
    case "stale_store_credit_hold":
    case "pos_store_credit_already_reversed":
    case "sale_not_editable":
      return "The sale or store-credit hold changed. Refresh the current state before retrying.";
    case "idempotency_conflict":
      return "This retry key was already used for different transaction details. Do not retry automatically.";
    case "sale_not_payable":
    case "cash_tender_insufficient":
      return error.message;
    case "register_not_open":
      return "Open or resume a register session before continuing.";
    case "payment_reconciliation_required":
    case "requires_reconciliation":
      return "This payment needs manager review in Operations before another payment attempt.";
    case "square_pos_app_not_configured":
      return "Square app payments need the Square POS application ID configured before this device handoff can be used.";
    default:
      return error.message || "Request failed.";
  }
}

function request(path: string, init: RequestInit, csrfToken: string): Promise<Response> {
  const method = init.method ?? "GET";
  return fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...(!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...init.headers,
    },
  });
}

function toStaffApiError(response: Response, data: unknown): StaffApiError {
  const body = isRecord(data) ? data : {};
  const error = isRecord(body.error) ? body.error : {};
  const code = typeof error.code === "string"
    ? error.code
    : response.status === 401
      ? "unauthorized"
      : response.status === 403
        ? "forbidden"
        : "request_failed";
  const message = typeof error.message === "string" ? error.message : "Request failed";
  return new StaffApiError({
    status: response.status,
    code,
    message,
    details: error.details,
    retryAfter: response.headers.get("retry-after") ?? undefined,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
